/**
 * Entry point of the React application. Sets up routing and global providers.
 * React 應用程式的進入點，負責設定路由與全域提供者。
 */
import ReactDOM from "react-dom/client";
import { store } from "./shared/store/store";
import { Provider } from "react-redux";
import App from "./App";
import Loading from "./components/ui/Loading";
import { markPageUnloading } from "./shared/api/axios";

// 多語系
import "./localization/i18n";

// 路由
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";

// Pages
import Home from "./features/home/pages/HomePage";
import Collection from "./features/home/pages/CollectionPage";
import Idol from "./features/idol/pages/IdolPage";
import IdolList from "./features/idol/pages/IdolList";
import IdolCreate from "./features/idol/pages/IdolCreate";
import IdolUpdate from "./features/idol/pages/IdolUpdate";
import PostDetail from "./features/posts/pages/PostDetail";
import Chats from "./features/chat/pages/ChatPage";
import WorldView from "./features/worldView/pages/WorldViewLayout";
import WorldCreate from "./features/worldView/pages/WorldCreate";
import Setting from "./features/settings/pages/SettingsPage";
import Account from "./features/settings/pages/AccountSettings";
import NotificationSettings from "./features/settings/pages/account/NotificationSettings";
import Activity from "./features/settings/pages/ActivitySettings";
import BlockList from "./features/settings/pages/BlockList";
import DeveloperSettings from "./features/settings/pages/DeveloperSettings";
import TokenManagement from "./features/settings/pages/developer/TokenManagement";
import WebhookSettings from "./features/settings/pages/developer/WebhookSettings";
import SubscriptionSettings from "./features/settings/pages/idol/SubscriptionSettings";
import ChatSettings from "./features/settings/pages/idol/ChatSettings";
import AnalyticsLayout from "./features/settings/pages/analytics/AnalyticsLayout";
import NotFound from "./pages/notFound";
import AppErrorPage from "./pages/AppErrorPage";
import Terms from "./pages/terms";
import Search from "./features/search/pages/SearchPage";
import Privacy from "./pages/privacy";
import ChatToken from "@/pages/chatToken";
import Cookies from "./pages/cookies";
import ContactUs from "./pages/contact";
import Rules from "./pages/rules";
import AuthRedirect from "./features/auth/pages/AuthRedirect";
import SignUpPage from "./features/auth/pages/SignUpPage";
import ForgetPasswordPage from "./features/auth/pages/ForgetPasswordPage";
import WalletPage from "./features/wallet/pages/WalletPage";
import SubscriptionPage from "./features/subscription/pages/SubscriptionPage";
import IdolsPage from "./features/idols/pages/IdolsPage";
import WorldsPage from "./features/worlds/pages/WorldsPage";
import MyWorldsPage from "./features/worlds/pages/MyWorldsPage";

// fontawesome
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";

// css（Bootstrap 由 main.scss 帶入自訂變數後編譯，不另外載入 dist css）
import "./assets/css/main.scss";

// 帶大型套件（swagger-ui、recharts）又少人用的頁面拆成獨立 chunk，進入該頁才下載
// 下載中的畫面：站內點擊由 App 依 useNavigation 顯示 Loading，直接開網址由 RouterProvider 的 fallbackElement 顯示
//
// 部署後舊分頁要的 chunk 檔名已不存在，載入會失敗：整頁前往導覽目標一次，拿新版。
// 用 sessionStorage 旗標限制每次失敗只重載一次（與網路速度無關）：重載後仍失敗就清掉旗標並 throw，交給 AppErrorPage；
// 離線時重載只會換成瀏覽器的離線頁，同樣直接 throw
const CHUNK_RELOAD_KEY = "chunk_reload_pending";
const lazyPage = (importPage) => async () => {
  try {
    const page = await importPage();
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    return { Component: page.default };
  } catch (error) {
    if (!navigator.onLine || sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw error;
    }
    console.error("lazyPage: chunk 載入失敗，重新載入頁面", error);
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    markPageUnloading();
    // data router 要等 lazy 載入完才更新網址，此時 state.location 仍是原頁，要前往的是 navigation.location
    // （lazy 只會在 router 建立後被呼叫，引用下方宣告的 router 沒問題）
    const target = router.state.navigation.location;
    // 目標與目前網址只差 hash（或相同，例如直接開網址）時 assign 只會頁內捲動、不會重新載入
    if (target.pathname + target.search === window.location.pathname + window.location.search) {
      window.location.reload();
    } else {
      window.location.assign(router.createHref(target));
    }
    // 等待跳轉，不讓錯誤頁先閃出來
    return new Promise(() => {});
  }
};

// Configure FontAwesome icon library
// 設定 FontAwesome 圖示庫
library.add(fab, fas, far);

// Define application routes
// 定義應用程式路由
const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    // App 本身 render 失敗時的最後防線（此時 App 已被替換掉）
    errorElement: <AppErrorPage />,
    children: [
      {
        // 無 path 的 layout route：頁面 render 失敗時，錯誤頁顯示在 App 的 <Outlet /> 內，
        // App（WebSocket、全域停權 modal 等）不會被卸載
        errorElement: <AppErrorPage />,
        children: [
          {
            path: "",
            element: <Home />,
          },
          {
            path: "sign-up",
            element: <SignUpPage />,
          },
          {
            path: "collection",
            element: <Collection />,
          },
          {
            path: "subscription",
            element: <SubscriptionPage />,
          },
          {
            path: "idols",
            element: <IdolsPage />,
          },
          {
            path: "worlds",
            element: <WorldsPage />,
          },
          {
            path: "my-worlds",
            element: <MyWorldsPage />,
          },
          {
            path: "idol/:idolUuid",
            element: <Idol />,
          },
          {
            path: "idol-list",
            element: <IdolList />,
          },
          {
            path: "idol-create",
            element: <IdolCreate />,
          },
          {
            path: "idol-update/:idolUuid",
            element: <IdolUpdate />,
          },
          {
            path: "post/:postUuid",
            element: <PostDetail />,
          },
          {
            path: "chats/:idolUuid?",
            element: <Chats />,
          },
          {
            path: "world-view/:worldUuid",
            element: <WorldView />,
          },
          {
            path: "world-create",
            element: <WorldCreate />,
          },
          {
            path: "settings",
            element: <Setting />,
            children: [
              {
                path: "",
                element: <Account />,
              },
              {
                path: "account",
                children: [
                  {
                    path: "",
                    element: <Account />,
                  },
                  {
                    path: "notification",
                    element: <NotificationSettings />,
                  },
                ],
              },
              {
                path: "activity",
                element: <Activity />,
              },
              {
                path: "block-list",
                element: <BlockList />,
              },
              {
                path: "idol",
                children: [
                  {
                    path: "subscription-settings",
                    element: <SubscriptionSettings />,
                  },
                  {
                    path: "chat-settings",
                    element: <ChatSettings />,
                  },
                  {
                    // 舊的收入儀表板路徑，保留導向避免既有書籤失效
                    path: "revenue-dashboard",
                    element: <Navigate to="/settings/analytics/income" replace />,
                  },
                ],
              },
              {
                path: "analytics",
                element: <AnalyticsLayout />,
                children: [
                  {
                    path: "",
                    element: <Navigate to="income" replace />,
                  },
                  {
                    path: "income",
                    lazy: lazyPage(() => import("./features/settings/pages/analytics/IncomeAnalytics")),
                  },
                  {
                    path: "fan",
                    lazy: lazyPage(() => import("./features/settings/pages/analytics/FanAnalytics")),
                  },
                ],
              },
              {
                path: "developer",
                element: <DeveloperSettings />,
                children: [
                  {
                    path: "",
                    element: <TokenManagement />,
                  },
                  {
                    path: "token",
                    element: <TokenManagement />,
                  },
                  {
                    path: "webhook",
                    element: <WebhookSettings />,
                  },
                ],
              },
            ],
          },
          {
            path: "search",
            element: <Search />,
          },
          {
            path: "wallet",
            element: <WalletPage />,
          },
          {
            path: "terms",
            element: <Terms />,
          },
          {
            path: "404",
            element: <NotFound />,
          },
          {
            path: "privacy",
            element: <Privacy />,
          },
          {
            path: "chat-token",
            element: <ChatToken />,
          },
          {
            path: "cookies",
            element: <Cookies />,
          },
          {
            path: "contact",
            element: <ContactUs />,
          },
          {
            path: "rules",
            element: <Rules />,
          },
          {
            path: "auth-redirect",
            element: <AuthRedirect />,
          },
          {
            path: "forget-password",
            element: <ForgetPasswordPage />,
          },
          {
            path: "api-docs",
            lazy: lazyPage(() => import("./pages/ApiDocs")),
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  },
]);

// Mount the React application
// 掛載 React 應用程式
ReactDOM.createRoot(document.getElementById("root")).render(
  // <React.StrictMode>
  <Provider store={store}>
    <RouterProvider router={router} fallbackElement={<Loading />} />
  </Provider>
  // </React.StrictMode>,
);

// React.StrictMode 會故意重複調用某些生命週期方法和 hooks，以幫助開發者發現潛在問題
